// SCR-HOME с 10 зонами (E06-T05), MOD-ZONE и MOD-ZONE-UPGRADED (E06-T06) — ux/03 §1.3, §4, §5.
// Уровни, стройка, цены и бонусы — только с сервера (GET /garage); таймер стройки — по времени сервера.
// MOD-ZONE-UPGRADED — один раз на завершённый уровень, в том числе если стройка кончилась, пока игра была закрыта.
import { signal } from '@preact/signals';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { Api, ResponseOf } from '@garages/contract';
import { useApp } from '../app/app.js';
import { bonusLines, ZONES } from '../content/garage.js';
import { duration, num, timer } from '../i18n/format.js';
import { t as tr } from '../i18n/t.js';
import { ApiCallError } from '../net/api-client.js';
import { online, player, serverNow, setWallet, tutorialStep, wallet } from '../state/game.js';
import { storage } from '../state/storage.js';
import type { SheetProps } from '../ui/App.js';
import { Sheet, Skeleton, useNow } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { I } from '../assets/icons.js';
import { setTut, TEXT, tutT07Upgrade } from '../tutorial/flow.js';
import { assetEntry, assetUrl, manifest } from '../assets/manifest.js';
import { opaqueBox, type Box } from '../assets/opaque.js';
import { carInDom, homeBg, homeCar } from '../scene/home-geo.js';
import { effect } from '@preact/signals';
import { stageUp } from '../ui/fx.js';
import { reduced } from '../ui/motion.js';
import { railBox, shiftLabels } from './home-rail.js';

type Zone = ResponseOf<Api['getGarage']>['zones'][number];
export const garage = signal<Zone[] | null>(null);
const SEEN_KEY = 'zoneSeenLevels';
const HINTS_KEY = 'zoneHintsSeen';
export const CANCEL_WINDOW_MS = 5 * 60_000;

export function zoneName(id: string): string { return ZONES[id]?.name ?? id; }

/** Начало стройки: сервер даёт срок окончания и длительность следующего уровня (Р-56). */
export function buildStartedAt(z: Zone): number | null {
  if (!z.upgradeFinishAt || !z.nextCost) return null;
  return Date.parse(z.upgradeFinishAt) - z.nextCost.timeS * 1000;
}

/** Завершённые уровни, которые игрок ещё не видел (MOD-ZONE-UPGRADED один раз). Первый запуск — без окон. */
export function newlyUpgraded(zones: Zone[], seen: Record<string, number> | null): Zone[] {
  if (!seen) return [];
  return zones.filter((z) => z.level > (seen[z.zoneId] ?? 0));
}

/**
 * Подсказка новой зоны — одна на уровень (ГД 03 §3, ux/03 §1.3): пульсирует только зона, открывшаяся на текущем
 * уровне (hintLevel === level), пока её не тронули или не построили. Старым непостроенным зонам — статичная стрелка.
 */
export function hintedZone(zones: Zone[], level: number, seen: string[]): string | null {
  const z = zones.find((x) => x.level === 0 && ZONES[x.zoneId]?.hintLevel === level && !seen.includes(x.zoneId));
  return z?.zoneId ?? null;
}

export async function loadGarage(app: ReturnType<typeof useApp>): Promise<void> {
  try {
    const g = await app.call('getGarage');
    garage.value = g.zones;
    const seen = storage.getJson<Record<string, number>>(SEEN_KEY);
    const fresh = newlyUpgraded(g.zones, seen);
    storage.setJson(SEEN_KEY, Object.fromEntries(g.zones.map((z) => [z.zoneId, z.level])));
    // Окно — только для стройки со временем (мгновенный T07 показывает обучение); по одному за раз.
    const first = fresh.find((z) => z.level > 0);
    // Окно — после сцены стройки на самой зоне (пыль, вспышка, подъём — 1.2 с, ui/fx.stageUp), а не поверх неё.
    if (first && tutorialStep.value !== 'T07') {
      setTimeout(() => { if (!app.nav.state.sheets.length) app.nav.openSheet('MOD-ZONE-UPGRADED', { zoneId: first.zoneId, level: first.level }); }, reduced() ? 0 : 1300);
    }
  } catch (e) {
    if (!(e instanceof ApiCallError && (e.code === 'NETWORK' || e.code === 'TIMEOUT'))) app.handleError(e, { resource: tr('CL-GARAGE-RES') });
  }
}

/** Слой зон поверх сцены гаража: плашка «название · ур.», стройка с таймером, «Готово», подсказка новой зоны. */
/**
 * Машина всегда над картинками зон (в том числе над воротами по bg_rect) и под их метками: машину-картинку рисует
 * этот слой, а не холст под DOM. Сцена двигает её (выезд, подвеска) и отдаёт прямоугольник в координатах холста;
 * здесь он переводится в координаты слоя — при каждом изменении и при любом ресайзе слоя или окна.
 */
function useCarImg(img: { current: HTMLImageElement | null }, layer: { current: HTMLDivElement | null }) {
  useEffect(() => {
    carInDom.value += 1;
    const place = () => {
      const el = img.current;
      const c = homeCar.value;
      const cv = document.querySelector('#stage canvas')?.getBoundingClientRect();
      const L = layer.current?.getBoundingClientRect();
      if (!el) return;
      if (!c || !cv || !L) { el.style.display = 'none'; return; }
      if (el.getAttribute('src') !== c.url) el.setAttribute('src', c.url);
      Object.assign(el.style, { display: 'block', left: `${cv.x - L.x + c.x}px`, top: `${cv.y - L.y + c.y}px`, width: `${c.w}px`, height: `${c.h}px`, opacity: String(c.alpha) });
    };
    const stop = effect(place);
    const ro = new ResizeObserver(place);
    if (layer.current) ro.observe(layer.current);
    const cvEl = document.querySelector('#stage canvas');
    if (cvEl) ro.observe(cvEl);
    addEventListener('resize', place);
    return () => { stop(); ro.disconnect(); removeEventListener('resize', place); carInDom.value -= 1; };
  }, []);
}

/** Пар идёт только от построенной печки (ux/13 §6.3), от верха её рисунка: точка — по непрозрачной рамке картинки. */
const STEAM_ZONE = 'G04';
/** Кнопка зоны — средние 72% × 76% картинки снизу (styles.css .zone-art): доли картинки пересчитываются в доли кнопки. */
const HIT_W = 0.72;
const HIT_H = 0.76;
function Steam(p: { box: Box }) {
  const x = ((p.box[0] + p.box[2]) / 2 - (1 - HIT_W) / 2) / HIT_W;
  const y = (p.box[1] - (1 - HIT_H)) / HIT_H;
  return <span class="zone-steam" aria-hidden style={{ left: `${x * 100}%`, top: `${y * 100}%` }}><i /><i /><i /></span>;
}

/** Уровни зон, которые игрок уже видел, — новая стадия проигрывается один раз (в том числе когда стройка кончилась без него). */
const seenLevels = new Map<string, number>();

export function ZoneLayer() {
  const app = useApp();
  const zones = garage.value;
  const now = useNow();
  const me = player.value;
  const [hintsSeen, setHintsSeen] = useState<string[]>(storage.getJson<string[]>(HINTS_KEY) ?? []);
  useEffect(() => {
    void loadGarage(app);
    const t = setInterval(() => { void loadGarage(app); }, 60_000);
    return () => clearInterval(t);
  }, []);
  // Стройка закончилась, пока экран открыт, — перечитать гараж в момент окончания.
  const finishing = zones?.find((z) => z.upgradeFinishAt);
  useEffect(() => {
    if (!finishing?.upgradeFinishAt) return;
    const left = Date.parse(finishing.upgradeFinishAt) - serverNow();
    const t = setTimeout(() => { void loadGarage(app); }, Math.max(500, left + 500));
    return () => clearTimeout(t);
  }, [finishing?.upgradeFinishAt]);
  // Новая стадия зоны (ux/13 §6.3): пыль, вспышка, новая картинка поднимается — 1.2 с, один раз.
  const layerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!zones) return;
    for (const z of zones) {
      const was = seenLevels.get(z.zoneId);
      if (was !== undefined && z.level > was) {
        const el = layerRef.current?.querySelector<HTMLElement>(`[data-testid="zone-${z.zoneId}"]`);
        if (el) { stageUp(el); app.haptic('success'); }
      }
      seenLevels.set(z.zoneId, z.level);
    }
  }, [zones]);
  // Стадии с bg_rect (ворота) заменяют нарисованное на фоне: непрозрачная рамка рисунка растягивается
  // в прямоугольник активного фона (BG-GARAGE или -XL). Нужны: где фон на экране, где слой и рамки картинок.
  const bg = homeBg.value;
  const [layerAt, setLayerAt] = useState<{ x: number; y: number } | null>(null);
  const [boxes, setBoxes] = useState<Record<string, Box>>({});
  useLayoutEffect(() => {
    const el = layerRef.current;
    if (!el) return;
    const measure = () => { const r = el.getBoundingClientRect(); setLayerAt((o) => (o && o.x === r.x && o.y === r.y ? o : { x: r.x, y: r.y })); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [!!zones, bg]);
  const fillOf = (zoneId: string, level: number) => {
    const id = `ZONE-${zoneId}-L${Math.max(1, Math.min(5, level))}`;
    const rect = bg ? assetEntry(id)?.bgRect?.[bg.id] : undefined;
    const url = rect ? assetUrl(id) : null;
    return rect && url ? { rect, url } : null;
  };
  useEffect(() => {
    for (const z of zones ?? []) {
      if (z.level < 1) continue;
      // Рамки нужны стадиям с bg_rect и печке (пар идёт от верха её рисунка).
      const url = fillOf(z.zoneId, z.level)?.url ?? (z.zoneId === STEAM_ZONE ? zoneArt(z.zoneId, z.level) : null);
      if (url && !boxes[url]) void opaqueBox(url).then((b) => setBoxes((o) => ({ ...o, [url]: b })));
    }
  }, [zones, bg]);
  const artsRef = useRef<HTMLDivElement>(null);
  const carRef = useRef<HTMLImageElement>(null);
  useCarImg(carRef, layerRef);
  // Точка зоны: на XL-фоне — pos_xl из манифеста (доли картинки фона) через cover и сдвиг фона; иначе — доли слоя.
  const spot = (zoneId: string, tag = false) => {
    const info = ZONES[zoneId]!;
    const xl = bg?.id === 'BG-GARAGE-XL' && layerAt ? zonePosXl(zoneId) : null;
    // Фон cover на высокой сцене обрезан по бокам: точку за краем держим в видимой части (60 px от края слоя).
    const lw = layerRef.current?.clientWidth ?? 0;
    const lh = layerRef.current?.clientHeight ?? 0;
    const keep = (v: number, max: number) => (max > 120 ? Math.max(60, Math.min(max - 60, v)) : v);
    const x = xl ? `${keep(bg!.x - layerAt!.x + xl[0] * bg!.w, lw)}px` : `${info.pos[0] * 100}%`;
    const y = xl ? `${keep(bg!.y - layerAt!.y + xl[1] * bg!.h, lh)}px` : `${info.pos[1] * 100}%`;
    return { left: tag ? `clamp(64px, ${x}, calc(100% - 64px))` : x, top: y };
  };
  // Колонка «Ещё» на главном стоит у правого края всегда; метки, которые в неё заходят, сдвигаются влево (по railBox).
  const rail = railBox.value;
  const [shifts, setShifts] = useState<Record<string, number>>({});
  useLayoutEffect(() => {
    const el = layerRef.current;
    if (!el) return;
    const labels = rail ? [...el.querySelectorAll<HTMLElement>('[data-shift-key]')].map((n) => {
      const key = n.dataset['shiftKey']!;
      const r = n.getBoundingClientRect();
      const cur = shifts[key] ?? 0; // мерим от исходного места, без текущего сдвига
      return { key, left: r.left + cur, right: r.right + cur, top: r.top, bottom: r.bottom };
    }) : [];
    const next = rail ? shiftLabels(labels, rail) : {};
    const ks = Object.keys(next);
    if (ks.length !== Object.keys(shifts).length || ks.some((k) => next[k] !== shifts[k])) setShifts(next);
  });
  const shifted = (k: string, st: Record<string, string>) => (shifts[k] ? { ...st, marginLeft: `${-shifts[k]!}px` } : st);
  if (!zones) return <div class="zone-layer" aria-busy="true" />;
  const hint = me ? hintedZone(zones, me.level, hintsSeen) : null;
  return (
    <div class="zone-layer" data-testid="zone-layer" ref={layerRef}>
      {(() => {
      const one = (z: Zone) => {
        const info = ZONES[z.zoneId];
        if (!info) return null;
        const left = z.upgradeFinishAt ? Date.parse(z.upgradeFinishAt) - now : 0;
        const ready = z.upgradeFinishAt && left <= 0;
        const canUp = !z.upgradeFinishAt && z.nextCost && me && me.level >= z.nextCost.playerLevel && (wallet.value?.nuts ?? 0) >= z.nextCost.nuts;
        // Построенная зона — арт стадии ZONE-Gxx-Ly (низ картинки в точке зоны) и короткая метка под ним;
        // непостроенная или без арта — маркер, как раньше.
        const art = z.level >= 1 ? zoneArt(z.zoneId, z.level) : null;
        const fill = art ? fillOf(z.zoneId, z.level) : null;
        const box = fill ? boxes[fill.url] : undefined;
        if (fill && box && bg && layerAt) {
          // Рисунок стадии во весь прямоугольник фона: картинка растягивается так, чтобы её рамка легла ровно в bg_rect.
          const [x0, y0, x1, y1] = fill.rect;
          const R = { x: bg.x - layerAt.x + x0 * bg.w, y: bg.y - layerAt.y + y0 * bg.h, w: (x1 - x0) * bg.w, h: (y1 - y0) * bg.h };
          const W = R.w / (box[2] - box[0]);
          const H = R.h / (box[3] - box[1]);
          return (
            <button
              key={z.zoneId}
              class={`zone-art fill ${hint === z.zoneId ? 'hint' : ''}`}
              data-testid={`zone-${z.zoneId}`}
              data-tut={`zone-${z.zoneId}`}
              style={{ left: `${R.x - box[0] * W}px`, top: `${R.y - box[1] * H}px`, width: `${W}px`, height: `${H}px` }}
              aria-label={tr('UI-GAR-ZONE-LVL', { zone: info.name, lvl: z.level })}
              onClick={() => { app.haptic('light'); app.nav.openSheet('MOD-ZONE', { zoneId: z.zoneId }); }}
            >
              <img src={fill.url} alt="" draggable={false} />
            </button>
          );
        }
        if (art) {
          return (
            <button
              key={z.zoneId}
              class={`zone-art ${hint === z.zoneId ? 'hint' : ''}`}
              data-testid={`zone-${z.zoneId}`}
              data-tut={`zone-${z.zoneId}`}
              style={spot(z.zoneId)}
              aria-label={tr('UI-GAR-ZONE-LVL', { zone: info.name, lvl: z.level })}
              onClick={() => { app.haptic('light'); app.nav.openSheet('MOD-ZONE', { zoneId: z.zoneId }); }}
            >
              <img src={art} alt="" draggable={false} />
              {z.zoneId === STEAM_ZONE && boxes[art] ? <Steam box={boxes[art]!} /> : null}
            </button>
          );
        }
        return (
          <button
            key={z.zoneId}
            class={`zone-pin ${z.level === 0 ? 'z0' : ''} ${hint === z.zoneId ? 'hint' : ''}`}
            data-testid={`zone-${z.zoneId}`}
            data-tut={`zone-${z.zoneId}`}
            data-shift-key={z.zoneId}
            style={shifted(z.zoneId, spot(z.zoneId))}
            aria-label={tr('UI-GAR-ZONE-LVL', { zone: info.name, lvl: z.level })}
            onClick={() => {
              app.haptic('light');
              if (hint === z.zoneId) { const s = [...hintsSeen, z.zoneId]; setHintsSeen(s); storage.setJson(HINTS_KEY, s); }
              app.nav.openSheet('MOD-ZONE', { zoneId: z.zoneId });
            }}
          >
            <span class="zone-name">{info.name}</span>
            <span class="zone-lvl">{z.upgradeFinishAt ? (ready ? tr('CL-GARAGE-READY') : timer(left)) : z.level ? tr('CL-GARAGE-LVL', { n: z.level }) : tr('CL-GARAGE-NEW')}</span>
            {canUp ? <span class="zone-up" aria-hidden>▲</span> : null}
            {z.upgradeFinishAt && !ready ? <span class="zone-scaffold" aria-hidden><I n="maint" size={14} /></span> : null}
          </button>
        );
      };
      // Картинки стадий — в своём слое, над ним машина (useCarImg), маркеры и метки — над машиной.
      const isArt = (z: Zone) => z.level >= 1 && !!zoneArt(z.zoneId, z.level);
      return (
        <>
          <div class="zone-arts" ref={artsRef}>{zones.filter(isArt).map(one)}</div>
          <img class="zone-car" ref={carRef} alt="" aria-hidden draggable={false} />
          {zones.filter((z) => !isArt(z)).map(one)}
        </>
      );
      })()}
      {/* Метки построенных зон — отдельным слоем поверх всех картинок: соседний арт их не закрывает. */}
      {zones.filter((z) => z.level >= 1 && zoneArt(z.zoneId, z.level) && ZONES[z.zoneId]).map((z) => {
        const info = ZONES[z.zoneId]!;
        const left = z.upgradeFinishAt ? Date.parse(z.upgradeFinishAt) - now : 0;
        const ready = z.upgradeFinishAt && left <= 0;
        const canUp = !z.upgradeFinishAt && z.nextCost && me && me.level >= z.nextCost.playerLevel && (wallet.value?.nuts ?? 0) >= z.nextCost.nuts;
        return (
          <span key={`t-${z.zoneId}`} class={`zone-tag ${hint === z.zoneId ? 'hint' : ''}`} data-shift-key={`t-${z.zoneId}`} style={shifted(`t-${z.zoneId}`, spot(z.zoneId, true))} aria-hidden>
            {z.upgradeFinishAt ? `${ready ? tr('CL-GARAGE-READY') : timer(left)} · ${info.name}` : `${info.name} · ${z.level}`}
            {canUp ? <span class="zone-up">▲</span> : null}
          </span>
        );
      })}
    </div>
  );
}

/** pos_xl зоны из манифеста: у любой её стадии ZONE-Gxx-L1…L5 (точка одна на зону). */
export function zonePosXl(zoneId: string): [number, number] | null {
  void manifest.value;
  for (let l = 1; l <= 5; l++) { const p = assetEntry(`ZONE-${zoneId}-L${l}`)?.posXl; if (p) return p; }
  return null;
}

/** Арт стадии зоны: уровень 1…5 → ZONE-Gxx-L1…L5; нет файла в манифесте — null (маркер). */
export function zoneArt(zoneId: string, level: number): string | null {
  void manifest.value;
  return assetUrl(`ZONE-${zoneId}-L${Math.max(1, Math.min(5, level))}`);
}

/** MOD-ZONE (ux/03 §4): цена, требование, время, бонус; стройка — ускорение и отмена в первые 5 мин. */
export function ZoneSheet({ entry }: SheetProps) {
  const app = useApp();
  const id = String(entry.params['zoneId']);
  const zones = garage.value;
  const z = zones?.find((x) => x.zoneId === id);
  const info = ZONES[id];
  const me = player.value;
  const w = wallet.value;
  const now = useNow();
  const key = useRef(app.idem());
  const [busy, setBusy] = useState(false);
  // T07 (ux/11 §1.3): первый уровень Верстака — бесплатно и мгновенно, уровень выдаёт сервер по ключу шага.
  const tutT07 = tutorialStep.value === 'T07' && id === 'G01';
  useEffect(() => { if (tutT07) setTut({ target: 'zone-upgrade', say: null, hint: TEXT.T07zone }); }, [tutT07]);
  if (!z || !info) return <Sheet entry={entry} title={zoneName(id)}><Skeleton h={120} /></Sheet>;
  const other = zones?.find((x) => x.upgradeFinishAt && x.zoneId !== id);
  const building = !!z.upgradeFinishAt;
  const left = building ? Date.parse(z.upgradeFinishAt!) - now : 0;
  const started = buildStartedAt(z);
  const canCancel = building && started !== null && now - started < CANCEL_WINDOW_MS && left > 0;
  const nc = z.nextCost;
  const lackNuts = nc ? Math.max(0, nc.nuts - (w?.nuts ?? 0)) : 0;
  const lowLevel = !!nc && !!me && me.level < nc.playerLevel;
  const act = async (fn: () => Promise<{ zone: Zone; wallet: ResponseOf<Api['getWallet']> }>, after?: () => void) => {
    setBusy(true);
    try {
      const r = await fn();
      key.current = app.idem();
      garage.value = (garage.value ?? []).map((x) => (x.zoneId === r.zone.zoneId ? r.zone : x));
      setWallet(r.wallet);
      after?.();
    } catch (e) {
      app.handleError(e, { resource: tr('CL-GARAGE-NUTS') });
      void loadGarage(app);
    } finally { setBusy(false); }
  };
  const build = () => act(() => app.call('upgradeZone', { params: { zoneId: id as 'G01' }, body: {}, idempotencyKey: key.current }), () => {
    app.haptic('medium');
    app.nav.popSheet();
  });
  const speedup = () => act(() => app.call('speedupZone', { params: { zoneId: id as 'G01' }, body: { payWith: 'talons' }, idempotencyKey: key.current }), () => { void loadGarage(app); });
  const cancel = () => app.nav.openSheet('MOD-CONFIRM', {
    title: tr('CL-GARAGE-CANCEL-TITLE'), text: tr('CL-GARAGE-CANCEL-TEXT'), risky: tr('CL-GARAGE-CANCEL'), safe: tr('CL-GARAGE-KEEP'),
    onRisky: () => act(async () => {
      const r = await app.call('cancelZone', { params: { zoneId: id as 'G01' }, body: {}, idempotencyKey: app.idem() });
      showToast(tr('CL-GARAGE-REFUNDED', { n: num(r.refunded.currencies?.nuts ?? 0) }), 'success');
      return r;
    }),
  });
  const off = !online.value;
  let actions: Parameters<typeof Sheet>[0]['actions'];
  if (off) actions = [{ text: tr('CL-GARAGE-OFFLINE'), disabled: true, onClick: () => {} }];
  else if (!nc && !building) actions = [];
  else if (building && left <= 0) actions = [{ text: tr('UI-ZONE-ACCEPT'), testId: 'zone-accept', onClick: () => { void loadGarage(app); app.nav.popSheet(); } }];
  else if (building) {
    actions = [
      ...(canCancel ? [{ text: tr('CL-GARAGE-CANCEL'), tone: 'danger' as const, testId: 'zone-cancel', onClick: cancel }] : []),
      { text: tr('CL-GARAGE-SPEEDUP'), icon: 'talons' as const, testId: 'zone-speedup', onClick: speedup },
    ];
  } else if (other) {
    actions = [
      { text: tr('CL-GARAGE-OTHER'), tone: 'outline' as const, onClick: () => app.nav.openSheet('MOD-ZONE', { zoneId: other.zoneId }, { replace: true }) },
      { text: tr('UI-ZONE-BUSY', { zone: zoneName(other.zoneId) }), disabled: true, onClick: () => {} },
    ];
  } else if (lowLevel) actions = [{ text: tr('CL-GARAGE-NEED-LEVEL', { n: nc!.playerLevel }), disabled: true, testId: 'zone-build', onClick: () => {} }];
  else if (lackNuts) actions = [{ text: tr('CL-GARAGE-LACK', { n: num(lackNuts) }), tone: 'outline' as const, testId: 'zone-build', onClick: () => app.nav.openSheet('MOD-RESOURCE-INFO', { currency: 'nuts' }) }];
  else actions = [{ text: tr('UI-ZONE-BUILD', { cost: num(nc!.nuts) }), icon: 'nuts' as const, testId: 'zone-build', disabled: busy, onClick: build }];
  if (tutT07 && z.level === 0) actions = [{ text: TEXT.T07btn, testId: 'zone-upgrade', disabled: busy, onClick: async () => { setBusy(true); try { await tutT07Upgrade(app); } finally { setBusy(false); } } }];
  return (
    <Sheet entry={entry} title={info.name} actions={actions}>
      <div class="col" data-testid="MOD-ZONE-body">
        <span class="muted">{z.level ? info.stages[z.level - 1] : info.desc}</span>
        <b>{tr('UI-ZONE-LEVEL', { n: z.level, max: 5 })}</b>
        <div class="card col" style={{ gap: 4 }}>
          {bonusLines(z.bonus).map((l) => <span key={l} class="pos">{l}</span>)}
          {!bonusLines(z.bonus).length ? <span class="muted small">{tr('CL-GARAGE-NO-BONUS')}</span> : null}
          {nc ? <span class="small muted">{tr('CL-GARAGE-NEXT', { stage: info.stages[z.level] ?? '' })}</span> : <span>{tr('UI-GAR-ZONE-MAX')}</span>}
        </div>
        {building ? (
          <div class="banner" data-testid="zone-timer">{left > 0 ? tr('CL-GARAGE-BUILDING', { t: timer(left) }) : tr('CL-GARAGE-READY')}</div>
        ) : nc ? (
          <div class="row small" style={{ gap: 16, flexWrap: 'wrap' }}>
            <span class={lackNuts ? 'neg' : ''}><I n="nuts" size={16} /> {num(nc.nuts)}</span>
            {nc.timeS ? <span>{tr('CL-GARAGE-TIME', { d: duration(nc.timeS * 1000) })}</span> : <span>{tr('CL-GARAGE-INSTANT')}</span>}
            <span class={lowLevel ? 'neg' : ''}>{tr('CL-GARAGE-NEED-LEVEL', { n: nc.playerLevel })}</span>
          </div>
        ) : null}
        {building && !canCancel && left > 0 ? <span class="small muted">{tr('CL-GARAGE-CANCEL-LATE')}</span> : null}
        <span class="small muted">{tr('UI-ZONE-GUESTS')}</span>
      </div>
    </Sheet>
  );
}

/** MOD-ZONE-UPGRADED (ux/03 §5): новая стадия и бонус, закрывается сам через 4 с. */
export function ZoneUpgradedSheet({ entry }: SheetProps) {
  const app = useApp();
  const id = String(entry.params['zoneId']);
  const level = Number(entry.params['level']);
  const z = garage.value?.find((x) => x.zoneId === id);
  const info = ZONES[id];
  useEffect(() => {
    app.haptic('success');
    const t = setTimeout(() => app.nav.popSheet(), 4000);
    return () => clearTimeout(t);
  }, []);
  return (
    <Sheet entry={entry} actions={[{ text: tr('CL-GARAGE-GREAT'), testId: 'zone-upgraded-ok', onClick: () => app.nav.popSheet() }]}>
      <div class="col" style={{ alignItems: 'center', textAlign: 'center' }} data-testid="MOD-ZONE-UPGRADED">
        <I n="sparks" size={48} />
        <h2 style={{ margin: 0 }}>{tr('UI-GAR-ZONE-LVL', { zone: info?.name ?? id, lvl: level })}</h2>
        <span class="muted">{info?.stages[level - 1]}</span>
        {bonusLines(z?.bonus).map((l) => <b key={l} class="pos">{l}</b>)}
      </div>
    </Sheet>
  );
}
